import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../components/Table";
import { usePersistStore, useStore } from "../../store";
import { PaymentService, TrustapUserService } from "../../services";
import {
  useSellerPayments,
  useTrustapConnectionHealth,
} from "../../hooks/queries";
import { Constants, Utils } from "../../utils";
import { Dropdown } from "react-bootstrap";
import ConfirmShippingPopup from "../../components/ConfirmShippingPopup";
import FullTrustapAccountRequiredPopup from "../../components/FullTrustapAccountRequiredPopup";
import PaymentStatusDropdown from "../../components/PaymentStatusDropdown";
import ShipmentStatusDropdown from "../../components/ShipmentStatusDropdown";
import { PAYMENT_STATUS, SHIPMENT_STATUS } from "../../utils/enums";
import {
  PaymentStatusChip,
  ShippingStatusChip,
} from "../../components/status";

function SellerPayments() {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [paymentStatus, setPaymentStatus] = useState<string>(
    PAYMENT_STATUS.ALL
  );
  const [shipmentStatus, setShipmentStatus] = useState<string>(
    SHIPMENT_STATUS.ALL
  );
  const { token } = usePersistStore((s: any) => s);
  const { setIsLoading, setErrorMessage, setSuccessMessage } = useStore(
    (s: any) => s
  );
  const { data: connectionHealth } = useTrustapConnectionHealth(token);
  // Connected seller whose grant predates a scope we now need: Start Shipping
  // and Claim Payout will 401 until they reconnect. Warn before they try.
  const needsTrustapReconnect = connectionHealth?.scopesUpToDate === false;
  const [selectedObj, setSelectedObj] = useState<any>(null);
  const [showConfirmShippingPopup, setShowConfirmShippingPopup] =
    useState(false);
  const [
    showFullTrustapAccountRequiredPopup,
    setShowFullTrustapAccountRequiredPopup,
  ] = useState(false);
  // Bump this to trigger a reload from any action handler without having to
  // hoist the `reload` callback above them.
  const [reloadKey, setReloadKey] = useState(0);
  // Per-row busy state so a second click on the same action is ignored
  // while the first request is still in flight.
  const [busyRowIds, setBusyRowIds] = useState<Set<string>>(new Set());
  const markBusy = useCallback((id: string, busy: boolean) => {
    setBusyRowIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleStartShipping = useCallback(async () => {
    if (!selectedObj?._id || !token) return;
    if (busyRowIds.has(selectedObj._id)) return;
    markBusy(selectedObj._id, true);
    setIsLoading(true);
    let result = await PaymentService.startShipping(token, {
      paymentId: selectedObj?._id,
    });

    if (result.error) {
      setErrorMessage(result.error);
      setIsLoading(false);
      markBusy(selectedObj._id, false);
      return;
    }
    setShowConfirmShippingPopup(false);
    const finishedId = selectedObj._id;
    setSelectedObj(null);
    setSuccessMessage("Shipping started successfully.");
    setIsLoading(false);
    markBusy(finishedId, false);
    setReloadKey((k) => k + 1);
  }, [
    token,
    setErrorMessage,
    setIsLoading,
    setSuccessMessage,
    setShowConfirmShippingPopup,
    selectedObj,
    setSelectedObj,
    busyRowIds,
    markBusy,
  ]);

  const handleTrackParcel = useCallback(
    async (row: any) => {
      if (!token || !row?._id) return;
      setIsLoading(true);
      let result = await PaymentService.trackShipment(token, {
        paymentId: row?._id,
      });

      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      const trackUrl = result?.data?.url;
      if (!trackUrl) {
        setErrorMessage("Tracking link is not available yet.");
        return;
      }
      const w = window.open(trackUrl, "_blank");
      if (!w) {
        setErrorMessage(
          "Your browser blocked the tracking window. Please allow popups and try again."
        );
      }
    },
    [token, setErrorMessage, setIsLoading]
  );

  const handleClaimPayout = useCallback(
    async (row: any) => {
      if (!token || !row?._id) return;
      if (busyRowIds.has(row._id)) return;
      markBusy(row._id, true);
      setIsLoading(true);

      // Trustap rejects /claim_for_seller with 401 when the seller only has
      // a guest account. Catch that here so we can show a helpful popup
      // instead of letting the API call fail mid-flight.
      const accountResult = await TrustapUserService.getMyTrustapAccount(token);
      if (accountResult.error) {
        setErrorMessage(accountResult.error);
        setIsLoading(false);
        markBusy(row._id, false);
        return;
      }
      const isFullAccount = accountResult?.data === true;
      if (!isFullAccount) {
        setIsLoading(false);
        markBusy(row._id, false);
        setShowFullTrustapAccountRequiredPopup(true);
        return;
      }

      let result = await PaymentService.claimPayout(token, {
        paymentId: row?._id,
      });

      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        markBusy(row._id, false);
        return;
      }

      setIsLoading(false);
      setSuccessMessage(
        "Transaction claimed successfully! Please check your Trustap account."
      );
      setSelectedObj(null);
      markBusy(row._id, false);
      setReloadKey((k) => k + 1);
    },
    [
      token,
      setErrorMessage,
      setIsLoading,
      setSuccessMessage,
      busyRowIds,
      markBusy,
    ]
  );

  // Temporary diagnostic for the Trustap claim_for_seller 401 bug. Hits
  // GET /payments/diagnose-claim/:id, prints the JSON to the console, and
  // toasts a hint pointing the user there. Remove once the underlying
  // claim issue is resolved.
  const handleDiagnoseClaim = useCallback(
    async (row: any) => {
      if (!token || !row?._id) return;
      const result = await PaymentService.diagnoseClaim(token, row._id);
      if (result.error) {
        setErrorMessage(
          typeof result.error === "string"
            ? result.error
            : "Diagnostic call failed."
        );
        return;
      }
      // eslint-disable-next-line no-console
      console.log("[diagnose-claim]", result.data);
      setSuccessMessage(
        "Diagnostic complete — see browser console for details."
      );
    },
    [token, setErrorMessage, setSuccessMessage]
  );

  const renderActions = useCallback(
    (row: any) => {
      // No seller-side actions are available until the buyer's payment is
      // confirmed by Trustap (still in the joined/awaiting-payment state).
      if (row?.status === "basic_tx.joined") return null;
      const isBusy = busyRowIds.has(row?._id);
      const canStartShipping = !row?.isShippingStarted;
      const canTrack =
        !!row?.isShippingStarted && !row?.isShippingCompleted;
      const canClaim = !!row?.isClaimable;
      if (!canStartShipping && !canTrack && !canClaim) return null;
      return (
        <div className="profileDropdown">
          <Dropdown>
            <Dropdown.Toggle aria-label="Row actions">
              <i className="bi bi-three-dots-vertical" aria-hidden="true" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {canStartShipping && (
                <Dropdown.Item
                  disabled={isBusy}
                  onClick={() => {
                    if (isBusy) return;
                    setSelectedObj(row);
                    setShowConfirmShippingPopup(true);
                  }}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-truck" aria-hidden="true" />
                    <span>Start Shipping</span>
                  </div>
                </Dropdown.Item>
              )}
              {canTrack && (
                <Dropdown.Item onClick={() => handleTrackParcel(row)}>
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-geo-alt" aria-hidden="true" />
                    <span>Track your parcel</span>
                  </div>
                </Dropdown.Item>
              )}
              {canClaim && (
                <Dropdown.Item
                  disabled={isBusy}
                  onClick={() => {
                    if (isBusy) return;
                    handleClaimPayout(row);
                  }}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-cash-coin" aria-hidden="true" />
                    <span>Claim transaction</span>
                  </div>
                </Dropdown.Item>
              )}
              {/*
                Temporary diagnostic button for the Trustap claim 401 bug.
                Hidden after the underlying issue was fixed by switching new
                transactions to use the seller's full Trustap user as
                seller_id. Keep this commented (and the handler / service /
                route intact) so we can re-enable quickly if the failure
                mode reappears in production.

                {canClaim && (
                  <Dropdown.Item onClick={() => handleDiagnoseClaim(row)}>
                    <div className="d-flex gap-2 align-items-center">
                      <i className="bi bi-bug" aria-hidden="true" />
                      <span>Diagnose claim</span>
                    </div>
                  </Dropdown.Item>
                )}
              */}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      );
    },
    [
      setSelectedObj,
      setShowConfirmShippingPopup,
      handleTrackParcel,
      handleClaimPayout,
      handleDiagnoseClaim,
      busyRowIds,
    ]
  );

  const renderPercentageChange = useCallback((row: any) => {
    return Utils.formatPercentChange(row?.adId?.price, row?.adId?.priceSold, 2);
  }, []);

  const renderBadge = useCallback(
    (row: any) => <PaymentStatusChip row={row} perspective="seller" />,
    []
  );

  const renderMobileCard = useCallback(
    (row: any) => (
      <div className="bidsCard">
        <div className="bidsCard__top">
          <div>
            <Link
              to={`/details/${row?.adId?._id}`}
              className="bidsCard__title"
            >
              {row?.adId?.title}
            </Link>
            {row?.code && (
              <div className="refCode refCode--sub">{row.code}</div>
            )}
          </div>
          <div className="bidsCard__status">{renderBadge(row)}</div>
        </div>
        <div className="bidsCard__grid">
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Opening</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.adId?.price)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Sold</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.adId?.priceSold)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Change</span>
            <span className="bidsCard__statValue">
              {renderPercentageChange(row)}
            </span>
          </div>
        </div>
        <div className="bidsCard__meta">
          <div className="bidsCard__metaRow">
            <i className="bi bi-calendar-event" aria-hidden="true"></i>
            <span>
              Paid {Utils.formatDateTime(row?.statusUpdatedAt || row?.createdAt)}
            </span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-truck" aria-hidden="true"></i>
            <span>
              Shipping:{" "}
              <ShippingStatusChip
                isStarted={!!row?.isShippingStarted}
                isCompleted={!!row?.isShippingCompleted}
              />
              {row?.shipmentCode && (
                <span className="refCode refCode--sub ms-2">
                  {row.shipmentCode}
                </span>
              )}
            </span>
          </div>
          {!row?.isShippingStarted && row?.shipmentDeadline && (
            <div className="bidsCard__metaRow">
              <i className="bi bi-clock-history" aria-hidden="true"></i>
              <span
                className={
                  row?.isShipmentOverdue ? "text-danger" : "text-warning"
                }
              >
                {row?.isShipmentOverdue ? "Overdue — " : "Ship by "}
                {Utils.formatDate(row.shipmentDeadline)}
              </span>
            </div>
          )}
        </div>
        {renderActions(row) && (
          <div className="bidsCard__actions">{renderActions(row)}</div>
        )}
      </div>
    ),
    [renderBadge, renderPercentageChange, renderActions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Code",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) =>
          row?.code ? (
            <span className="refCode">{row.code}</span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>—</span>
          ),
      },
      {
        title: "Title",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link
            to={`/details/${row?.adId?._id}`}
            style={{ color: "var(--main-color)", textDecoration: "none" }}
          >
            {row?.adId?.title}
          </Link>
        ),
      },
      {
        title: "Opening Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.adId?.price),
      },
      {
        title: "Selling Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.adId?.priceSold),
      },
      {
        title: "% Change",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => renderPercentageChange(row),
      },
      {
        title: "Payment Date",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) =>
          Utils.formatDateTime(row?.statusUpdatedAt || row?.createdAt),
      },
      {
        title: "Status",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderBadge(row),
      },
      {
        title: "Shipping",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => (
          <div className="d-flex flex-column align-items-center gap-1">
            <ShippingStatusChip
              isStarted={!!row?.isShippingStarted}
              isCompleted={!!row?.isShippingCompleted}
            />
            {row?.shipmentCode && (
              <span className="refCode refCode--sub">
                {row.shipmentCode}
              </span>
            )}
            {!row?.isShippingStarted && row?.shipmentDeadline && (
              <span
                className={
                  row?.isShipmentOverdue ? "text-danger" : "text-warning"
                }
                style={{ fontSize: 12, lineHeight: 1.2 }}
                title={`You must start shipping within ${Constants.SHIPMENT_DEADLINE_BUSINESS_DAYS} business days of payment.`}
              >
                {row?.isShipmentOverdue ? "Overdue — " : "Ship by "}
                {Utils.formatDate(row.shipmentDeadline)}
              </span>
            )}
          </div>
        ),
      },
      {
        title: "Actions",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderActions(row),
      },
    ],
    [renderPercentageChange, renderBadge, renderActions]
  );

  const { data, isLoading, error, refetch } = useSellerPayments(token, {
    page,
    limit: rowsPerPage,
    paymentStatus,
    shipmentStatus,
  });
  const list = data?.list || [];
  const count = data?.count || 0;
  const isDone = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  // Action handlers bump `reloadKey` to request a refresh after a mutation.
  useEffect(() => {
    if (reloadKey > 0) refetch();
  }, [reloadKey, refetch]);

  const onPaymentStatusChange = useCallback((v: string) => {
    setPage(0);
    setPaymentStatus(v);
  }, []);
  const onShipmentStatusChange = useCallback((v: string) => {
    setPage(0);
    setShipmentStatus(v);
  }, []);

  return (
    <>
      <ConfirmShippingPopup
        show={showConfirmShippingPopup}
        onNo={() => {
          setShowConfirmShippingPopup(false);
          setSelectedObj(null);
        }}
        onYes={handleStartShipping}
      />
      <FullTrustapAccountRequiredPopup
        show={showFullTrustapAccountRequiredPopup}
        onClose={() => setShowFullTrustapAccountRequiredPopup(false)}
      />
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4">
              <div className="tableCardHeader px-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">Payments</h2>
                  <p className="tableCardHeader__subtitle m-0">
                    {isDone
                      ? `${count} ${count === 1 ? "payment" : "payments"} total`
                      : "Loading your payments…"}
                  </p>
                </div>
                <div className="tableCardHeader__actions customTitle">
                  <PaymentStatusDropdown
                    value={paymentStatus}
                    onChange={onPaymentStatusChange}
                    includeFailed={false}
                  />
                  <ShipmentStatusDropdown
                    value={shipmentStatus}
                    onChange={onShipmentStatusChange}
                  />
                </div>
              </div>
              {needsTrustapReconnect && (
                <div
                  className="alert alert-warning d-flex align-items-start gap-2 mx-4 p-3"
                  role="alert"
                >
                  <i
                    className="bi bi-exclamation-triangle-fill mt-1"
                    aria-hidden="true"
                  />
                  <div>
                    <strong>Reconnect your Trustap account to keep selling.</strong>{" "}
                    Your connection is missing permissions we now need —{" "}
                    <strong>Start Shipping</strong> and{" "}
                    <strong>Claim Payout</strong> will fail until you reconnect.{" "}
                    <Link to="/dashboard/trustap-account">
                      Reconnect your Trustap account
                    </Link>
                    .
                  </div>
                </div>
              )}
              <Table
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={page}
                setPage={setPage}
                columns={columns}
                rows={list}
                isDone={isDone}
                count={count}
                mobileCard={renderMobileCard}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SellerPayments;
